// Dashboard slice — the data seam for the dashboard screen. Lives centrally in
// store/slices (one slice file per page). Fetches the backend /dashboard for the
// selected account and maps it to the UI shape. The `useDashboardData` hook
// (dashboard module) wraps this so the page stays presentational.
//
// The dashboard has NO mock branch. Its fixture data lives in the backend
// (`pcs-backend/backend/data/dashboard.json`, generated by `scripts/generate_data.py`)
// and reaches the app the same way production data does. The frontend previously
// carried a second, snake_case copy of the same payload behind
// `REACT_APP_USE_MOCKS`; the two shapes drifted and mock mode silently rendered a
// degraded hero while live mode was correct. One shape, one path, no drift.
// (Portfolio / Manage still have their own mocks — this change was scoped
// to the dashboard.)
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet } from '../../services/api/client';
import { getReadinessProjection, getDistributionDetails } from '../../services/api/dashboard';
import { mapDashboardResponse, mapProjection, mapDistributionDetails } from '../../components/modules/dashboard/dashboard.adapters';

export const fetchDashboard = createAsyncThunk(
  'dashboard/fetch',
  async (accountId, { rejectWithValue }) => {
    try {
      if (!accountId) return rejectWithValue('No account selected.');
      const data = await apiGet(`/dashboard?accountId=${encodeURIComponent(accountId)}`);
      return mapDashboardResponse(data);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load your dashboard.');
    }
  },
  // Dedupe (single-fetch guard): skip when the SAME request is already in flight
  // OR already loaded. This collapses both the React StrictMode double-fire (dev
  // mount→unmount→remount runs useDashboardData's fetch effect twice) and any
  // post-resolve remount into a single /dashboard call. Switching the selected
  // account (new accountId), or a retry after a failure, still proceeds. See
  // FRONTEND_RULES §7 (single-fetch guard).
  {
    condition: (accountId, { getState }) => {
      const s = getState().dashboard;
      if ((s.status === 'loading' || s.status === 'succeeded') && s.lastArg === accountId) return false;
      return true;
    },
  },
);

// One readiness projection at a picked retirement age. The page load carries only the
// default age (one metered iJoin call); each other age the participant picks is fetched
// here and merged into `data.readiness.projections` so the card renders it exactly as
// it renders the shipped one. Keyed per age so two clicks on the same age cost one call.
export const fetchReadinessProjection = createAsyncThunk(
  'dashboard/fetchReadinessProjection',
  async ({ accountId, age }, { rejectWithValue }) => {
    try {
      const row = await getReadinessProjection(accountId, age);
      return { age: String(age), projection: mapProjection(row) };
    } catch (err) {
      return rejectWithValue({ age: String(age), message: err.message || 'Projection unavailable.' });
    }
  },
  {
    condition: ({ accountId, age }, { getState }) => {
      const s = getState().dashboard;
      if (s.lastArg !== accountId || !s.data?.readiness) return false; // not this account's card
      const key = String(age);
      if (s.data.readiness.projections?.[key]) return false; // already have it
      return s.data.readiness.projectionStatus?.[key] !== 'loading';
    },
  },
);

// The tracker's "View details" popup: one record per request, fetched when the popup
// opens (GET /dashboard/distributions/{id}) and kept for the account so reopening is
// free. Keyed by requestId; each entry is { status, data, error }. Cleared with the
// account (a new /dashboard fetch), since the ids are the account's own.
export const fetchDistributionDetails = createAsyncThunk(
  'dashboard/fetchDistributionDetails',
  async ({ accountId, requestId }, { rejectWithValue }) => {
    try {
      const row = await getDistributionDetails(accountId, requestId);
      return { requestId, details: mapDistributionDetails(row) };
    } catch (err) {
      return rejectWithValue({ requestId, message: err.message || 'Request details unavailable.' });
    }
  },
  {
    condition: ({ accountId, requestId }, { getState }) => {
      const s = getState().dashboard;
      if (s.lastArg !== accountId) return false; // not this account's tracker
      const entry = s.distributionDetails[requestId];
      return !entry || entry.status === 'failed'; // loaded or in flight → skip; a failure retries
    },
  },
);

const initialState = {
  data: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  lastArg: undefined, // the accountId the current data is for — drives the single-fetch guard
  distributionDetails: {}, // requestId -> { status, data, error } for the "View details" popup
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboard.pending, (state, action) => { state.status = 'loading'; state.error = null; state.lastArg = action.meta.arg; state.distributionDetails = {}; })
      .addCase(fetchDashboard.fulfilled, (state, action) => { state.status = 'succeeded'; state.data = action.payload; })
      .addCase(fetchDashboard.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload || 'Failed to load dashboard'; })
      .addCase(fetchReadinessProjection.pending, (state, action) => {
        const r = state.data?.readiness;
        if (!r) return;
        r.projectionStatus = { ...(r.projectionStatus || {}), [String(action.meta.arg.age)]: 'loading' };
      })
      .addCase(fetchReadinessProjection.fulfilled, (state, action) => {
        const r = state.data?.readiness;
        if (!r) return;
        r.projections = { ...(r.projections || {}), [action.payload.age]: action.payload.projection };
        r.projectionStatus = { ...(r.projectionStatus || {}), [action.payload.age]: 'succeeded' };
      })
      .addCase(fetchReadinessProjection.rejected, (state, action) => {
        const r = state.data?.readiness;
        if (!r) return;
        const age = action.payload?.age ?? String(action.meta.arg.age);
        r.projectionStatus = { ...(r.projectionStatus || {}), [age]: 'failed' };
      })
      .addCase(fetchDistributionDetails.pending, (state, action) => {
        state.distributionDetails[action.meta.arg.requestId] = { status: 'loading', data: null, error: null };
      })
      .addCase(fetchDistributionDetails.fulfilled, (state, action) => {
        state.distributionDetails[action.payload.requestId] = { status: 'succeeded', data: action.payload.details, error: null };
      })
      .addCase(fetchDistributionDetails.rejected, (state, action) => {
        const id = action.payload?.requestId ?? action.meta.arg.requestId;
        state.distributionDetails[id] = { status: 'failed', data: null, error: action.payload?.message || 'Request details unavailable.' };
      });
  },
});

export const selectDashboardData = (state) => state.dashboard.data;
export const selectDashboardStatus = (state) => state.dashboard.status;
export const selectDashboardError = (state) => state.dashboard.error;
export const selectDistributionDetails = (state) => state.dashboard.distributionDetails;

export default dashboardSlice.reducer;
